import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ITrajectoryDetailProgress,
  TrajectorySectionCode,
} from '../../../../config/api/models/Trajectory';
import { useUser } from '../../../shared/hooks';
import { Button, Icons } from '../../../shared/components';
import { usePagesByType } from '../../hooks';
import { GroupStudentSeriesScores } from '../../../../config/api/models/Score';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import { UrlParams } from '../../../shared/types';
import { resetScrollPosition } from '../../helpers';
import TheoryOpbouwBlocksList from './TheoryOpbouwBlocksList';
import { useAppDispatch, useAppSelector } from '../../../shared/hooks/useAppDispatch';
import { fetchBlocks } from '../../../shared/slices/blocks/api/blocks';
import { usePatchPageProgressMutation } from 'src/modules/shared/slices/score-progress/progress';

type Props = {
  type: 'theory' | 'opbouw';
  onSelectedSectionChange: (newSectionCode: TrajectorySectionCode) => void;
  hasTheory: boolean;
  hasExercises: boolean;
  progress?: ITrajectoryDetailProgress[];
  onPatchProgress: (nextSectionCode: TrajectorySectionCode) => void;
  trajectoryScoresByGroup?: GroupStudentSeriesScores;
};

const userHasCompletedProgressOfType = (
  type: 'theory' | 'opbouw',
  progress?: ITrajectoryDetailProgress[]
): boolean => {
  if (!progress) return false;
  return progress.some((progress) => progress.type === type && progress.isCompleted);
};

const TrajectoryTheoryOpbouwPageView: FC<Props> = ({
  type,
  onSelectedSectionChange,
  hasTheory,
  hasExercises,
  progress,
  onPatchProgress,
  trajectoryScoresByGroup,
}) => {
  const { t } = useTranslation();
  const { isStudent } = useUser();
  const { bookId, chapterId, trajectoryId, sectionCode } = useParams<UrlParams>();
  const [nextSectionCode, setNextSectionCode] = useState<TrajectorySectionCode | ''>('');
  const log = useDataLogging();
  const pages = usePagesByType(type);
  const pageId = useMemo(() => {
    return pages.data && pages.data.length > 0 ? pages.data[0].id : undefined;
  }, [pages.data]);
  const [
    patchPageProgress,
    { isError: patchPageProgressError, isSuccess: patchPageProgresSuccess },
  ] = usePatchPageProgressMutation();

  const handleSectionDoneButton = useCallback(() => {
    if (pages.data && pages.data.length > 0) {
      if (type === 'opbouw') {
        if (hasTheory) {
          setNextSectionCode('1');
        } else if (hasExercises) {
          setNextSectionCode('2');
        }
        patchPageProgress({ trajectoryId: trajectoryId!, pageId: pageId! });
      } else {
        if (hasExercises) setNextSectionCode('2');
        patchPageProgress({ trajectoryId: trajectoryId!, pageId: pageId! });
      }
    }
    log(LogActionTypes.STUDENT_UNDERSTANDS_SECTION, {
      bookId,
      chapterId,
      trajectoryId,
      pageId: pageId || '',
    });
  }, [
    pages,
    type,
    hasTheory,
    hasExercises,
    patchPageProgress,
    setNextSectionCode,
    trajectoryId,
    log,
    bookId,
    chapterId,
    pageId,
  ]);

  useEffect(() => {
    // if errorCode 409 is returned, continue (this means the progress was already finished before)
    if (patchPageProgresSuccess || patchPageProgressError) {
      if (nextSectionCode !== '') {
        onPatchProgress(nextSectionCode);
        resetScrollPosition();
        setNextSectionCode('');
      } else {
        onPatchProgress(sectionCode as TrajectorySectionCode);
        resetScrollPosition();
        setNextSectionCode('');
      }
    }
  }, [
    patchPageProgresSuccess,
    patchPageProgressError,
    onSelectedSectionChange,
    setNextSectionCode,
    nextSectionCode,
    onPatchProgress,
    sectionCode,
  ]);

  const dispatch = useAppDispatch();
  const blocks = useAppSelector((state) => state.blocks.blocks);
  const loading = useAppSelector((state) => state.blocks.loading);

  useEffect(() => {
    if (!pageId) return;
    dispatch(fetchBlocks(pageId as string));
  }, [pageId, dispatch]);

  return (
    <>
      {!!pageId && blocks && blocks.length > 0 && !loading && (
        <TheoryOpbouwBlocksList
          blocks={blocks}
          pageId={pageId}
          type={type}
          trajectoryScoresByGroup={trajectoryScoresByGroup}
        />
      )}
      {isStudent && !userHasCompletedProgressOfType(type, progress) && (
        <div>
          <Button icon={Icons.SUBMIT} onClick={handleSectionDoneButton}>
            {t('trajectory.sections.shared.sectionDoneBtn')}
          </Button>
        </div>
      )}
    </>
  );
};

export default TrajectoryTheoryOpbouwPageView;
